import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://gatornotes.org";

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: "website" | "article" | "product";
  jsonLd?: Record<string, any> | Record<string, any>[];
  noindex?: boolean;
}

/**
 * Per-route SEO head tags. Overrides the static index.html defaults
 * for JS-executing crawlers (Googlebot). Static index.html still
 * serves as fallback for non-JS social crawlers.
 *
 * When `path` is omitted, canonical/og:url default to the current route's
 * pathname so every page self-references instead of pointing at the homepage.
 */
export function SEO({ title, description, path, image, type = "website", jsonLd, noindex }: SEOProps) {
  const location = useLocation();
  const resolvedPath = path ?? location.pathname;
  const url = `${SITE_URL}${resolvedPath}`;
  // Clamp title and description to safe lengths
  const safeTitle = title.length > 60 ? `${title.slice(0, 57)}…` : title;
  const safeDesc = description.length > 160 ? `${description.slice(0, 157)}…` : description;
  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{safeTitle}</title>
      <meta name="description" content={safeDesc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={safeDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      {image && <meta property="og:image" content={image} />}

      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={safeDesc} />
      {image && <meta name="twitter:image" content={image} />}

      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}
